// src/pages/orders/components/CustomerInformationSection.jsx
import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  User,
  MapPin,
  Mail,
  FileText,
  Plus,
  Minus,
  Star,
  X,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import PhoneNumberInput from '../../../components/ui/PhoneNumberInput/PhoneNumberInput';
import { checkBlacklistedPhones } from '../../../services/api/phoneBlacklist';
import { phoneLookupByPhone } from '../../../services/api/orders';
import CityDeliverySection from './shared/CityDeliverySection';
import styles from './CustomerInformationSection.module.css';

const CustomerInformationSection = ({
  formData,
  requiredFields = {},
  cities,
  errors,
  isCalculatingDelivery,
  onFieldChange,
  onPhoneNumberChange,
  onPhoneValidationChange,
  onAddPhoneNumber,
  onRemovePhoneNumber,
  onTogglePrimaryPhone,
  onAutofillCustomer,
  onRevertAutofill,
}) => {
  const { t } = useLocalization();
  const fieldLabel = (field, labelKey) => `${t(labelKey)}${requiredFields[field] ? ' *' : ''}`;
  const fieldPlaceholder = (field, placeholderKey) => t(requiredFields[field] ? `${placeholderKey}Required` : placeholderKey);
  const blacklistTimerRef = useRef(null);
  const lookupTimerRef = useRef(null);

  // Map of phone index -> { isBlacklisted, reason }
  const [blacklistWarnings, setBlacklistWarnings] = useState({});
  // Banner shown after autofill: null | 'pos_customer' | 'ecom_order'
  const [autofillSource, setAutofillSource] = useState(null);
  // Phone that produced the current autofill — changing it away resets the autofill
  const autofilledPhoneRef = useRef(null);

  // Debounced blacklist check — fires 600ms after any phone number changes
  const runBlacklistCheck = useCallback((phoneNumbers) => {
    if (blacklistTimerRef.current) clearTimeout(blacklistTimerRef.current);

    blacklistTimerRef.current = setTimeout(async () => {
      const toCheck = phoneNumbers
        .map((p, i) => ({ index: i, number: p.phoneNumber?.trim() }))
        .filter(({ number }) => number && number.length >= 9);

      if (toCheck.length === 0) {
        setBlacklistWarnings({});
        return;
      }

      try {
        const phones = toCheck.map(({ number }) => number);
        const result = await checkBlacklistedPhones(phones);
        const blacklistedMap = {};
        (result.blacklisted || []).forEach(({ phoneNumber, reason }) => {
          blacklistedMap[phoneNumber] = reason;
        });

        const warnings = {};
        toCheck.forEach(({ index, number }) => {
          if (blacklistedMap.hasOwnProperty(number)) {
            warnings[index] = { isBlacklisted: true, reason: blacklistedMap[number] };
          }
        });
        setBlacklistWarnings(warnings);
      } catch {
        // Silently ignore — blacklist check is advisory, never block the form
      }
    }, 600);
  }, []);

  useEffect(() => {
    runBlacklistCheck(formData.phoneNumbers);
    return () => {
      if (blacklistTimerRef.current) clearTimeout(blacklistTimerRef.current);
    };
  }, [formData.phoneNumbers, runBlacklistCheck]);

  // Debounced phone lookup — fires 700ms after the first phone number changes
  const firstPhone = formData.phoneNumbers[0]?.phoneNumber || '';
  useEffect(() => {
    if (!onAutofillCustomer) return;
    if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);

    const trimmed = firstPhone.trim();
    if (autofilledPhoneRef.current && autofilledPhoneRef.current !== trimmed) {
      autofilledPhoneRef.current = null;
      setAutofillSource(null);
      onRevertAutofill?.();
    }
    if (trimmed.length < 9) return;

    lookupTimerRef.current = setTimeout(async () => {
      try {
        const result = await phoneLookupByPhone(trimmed);
        if (result.found) {
          onAutofillCustomer({
            name: result.name,
            address: result.address,
            cityId: result.cityId,
            email: result.email,
            additionalPhones: result.additionalPhones || [],
          });
          autofilledPhoneRef.current = trimmed;
          setAutofillSource(result.source);
        }
      } catch {
        // Silently ignore — phone lookup is advisory
      }
    }, 700);

    return () => {
      if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPhone, onAutofillCustomer]);

  return (
    <div className={styles.customerInfoContent}>
      {/* Phone Numbers Section — shown first */}
      <div className={styles.phoneNumbersSection}>
        <div className={styles.sectionHeader}>
          <h3>{t('ecomOrders.phoneNumbers')} *</h3>
          <button type="button" onClick={onAddPhoneNumber} className={styles.addButton}>
            <Plus size={14} />
            {t('ecomOrders.addPhoneNumber')}
          </button>
        </div>

        {/* Autofill banner */}
        {autofillSource && (
          <div className={styles.autofillBanner}>
            <span className={styles.autofillBannerText}>
              <CheckCircle size={14} />
              {t('ecomOrders.phoneLookupAutofilled', {
                source: autofillSource === 'pos_customer'
                  ? t('ecomOrders.phoneLookupSourcePos')
                  : t('ecomOrders.phoneLookupSourceOrder'),
              })}
            </span>
            <button
              type="button"
              className={styles.autofillDismiss}
              onClick={() => setAutofillSource(null)}
              title="Dismiss"
            >
              <X size={13} />
            </button>
          </div>
        )}

        <div className={styles.phoneNumbersList}>
          {formData.phoneNumbers.map((phone, index) => (
            <div key={index} className={styles.phoneNumberItem}>
              <div className={styles.phoneInputContainer}>
                <PhoneNumberInput
                  id={`phone-${index}`}
                  name={`phoneNumbers[${index}]`}
                  value={phone.phoneNumber}
                  onChange={(value) => onPhoneNumberChange(index, value)}
                  onValidationChange={(hasError, errorMessage) =>
                    onPhoneValidationChange(index, hasError, errorMessage)
                  }
                  placeholder={t('ecomOrders.phoneNumberPlaceholder')}
                  required={index === 0}
                  error={errors.phoneNumberDetails?.[index] || (errors.phoneNumbers && index === 0 ? errors.phoneNumbers : '')}
                />
                {blacklistWarnings[index]?.isBlacklisted && (
                  <div className={styles.blacklistWarning}>
                    <AlertTriangle size={12} />
                    <span>
                      {blacklistWarnings[index].reason
                        ? t('phoneBlacklist.blacklistedWarningWithReason').replace('{{reason}}', blacklistWarnings[index].reason)
                        : t('phoneBlacklist.blacklistedWarning')}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.phoneActions}>
                <button
                  type="button"
                  onClick={() => onTogglePrimaryPhone(index)}
                  className={`${styles.primaryButton} ${phone.isPrimary ? styles.active : ''}`}
                  title={t('ecomOrders.setPrimaryPhone')}
                >
                  <Star size={13} fill={phone.isPrimary ? 'currentColor' : 'none'} />
                  {phone.isPrimary && <span className={styles.primaryLabel}>{t('ecomOrders.primaryPhone')}</span>}
                </button>
                {formData.phoneNumbers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemovePhoneNumber(index)}
                    className={styles.removeButton}
                    title={t('ecomOrders.removePhoneNumber')}
                  >
                    <Minus size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {errors.phoneNumbers && formData.phoneNumbers.length === 0 && (
          <span className={styles.errorText}>{errors.phoneNumbers}</span>
        )}
      </div>

      <div className={styles.formGrid}>
        {/* Customer Name */}
        <div className={styles.formGroup}>
          <label htmlFor="customerName">
            {t('ecomOrders.customerName')} *
          </label>
          <div className={styles.inputWrapper}>
            <User className={styles.inputIcon} size={16} />
            <input
              type="text"
              id="customerName"
              value={formData.customerName}
              onChange={(e) => onFieldChange('customerName', e.target.value)}
              placeholder={t('ecomOrders.customerNamePlaceholder')}
              className={errors.customerName ? styles.error : ''}
              required
            />
          </div>
          {errors.customerName && <span className={styles.errorText}>{errors.customerName}</span>}
        </div>

        {/* Private Reference */}
        <div className={styles.formGroup}>
          <label htmlFor="privateReference">
            {fieldLabel('privateReference', 'ecomOrders.privateReference')}
          </label>
          <div className={styles.inputWrapper}>
            <FileText className={styles.inputIcon} size={16} />
            <input
              type="text"
              id="privateReference"
              value={formData.privateReference}
              onChange={(e) => onFieldChange('privateReference', e.target.value)}
              placeholder={fieldPlaceholder('privateReference', 'ecomOrders.privateReferencePlaceholder')}
              className={errors.privateReference ? styles.error : ''}
            />
          </div>
          {errors.privateReference && <span className={styles.errorText}>{errors.privateReference}</span>}
        </div>

        {/* Delivery Address */}
        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
          <label htmlFor="address">
            {t('ecomOrders.address')} *
          </label>
          <div className={styles.inputWrapper}>
            <MapPin className={`${styles.inputIcon} ${styles.textareaIcon}`} size={16} />
            <textarea
              id="address"
              value={formData.address}
              onChange={(e) => onFieldChange('address', e.target.value)}
              placeholder={t('ecomOrders.addressPlaceholder')}
              className={errors.address ? styles.error : ''}
              rows={2}
              required
            />
          </div>
          {errors.address && <span className={styles.errorText}>{errors.address}</span>}
        </div>

        {/* Delivery City */}
        <CityDeliverySection
          cityId={formData.cityId}
          cities={cities}
          error={errors.cityId}
          isCalculatingDelivery={isCalculatingDelivery}
          onCityChange={(cityId) => onFieldChange('cityId', cityId)}
        />

        {/* Customer Email */}
        <div className={styles.formGroup}>
          <label htmlFor="customerEmail">
            {fieldLabel('customerEmail', 'ecomOrders.customerEmail')}
          </label>
          <div className={styles.inputWrapper}>
            <Mail className={styles.inputIcon} size={16} />
            <input
              type="email"
              id="customerEmail"
              value={formData.customerEmail}
              onChange={(e) => onFieldChange('customerEmail', e.target.value)}
              placeholder={fieldPlaceholder('customerEmail', 'ecomOrders.customerEmailPlaceholder')}
              className={errors.customerEmail ? styles.error : ''}
            />
          </div>
          {errors.customerEmail && <span className={styles.errorText}>{errors.customerEmail}</span>}
        </div>
      </div>

      {/* Notes Section */}
      <div className={styles.notesSection}>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="deliveryNotes">
              {fieldLabel('deliveryNotes', 'ecomOrders.deliveryNotes')}
            </label>
            <div className={styles.inputWrapper}>
              <FileText className={`${styles.inputIcon} ${styles.textareaIcon}`} size={16} />
              <textarea
                id="deliveryNotes"
                value={formData.deliveryNotes}
                onChange={(e) => onFieldChange('deliveryNotes', e.target.value)}
                placeholder={fieldPlaceholder('deliveryNotes', 'ecomOrders.deliveryNotesPlaceholder')}
                className={errors.deliveryNotes ? styles.error : ''}
                rows={2}
                maxLength={40}
              />
            </div>
            {errors.deliveryNotes && <span className={styles.errorText}>{errors.deliveryNotes}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="specialNotes">
              {fieldLabel('specialNotes', 'ecomOrders.specialNotes')}
            </label>
            <div className={styles.inputWrapper}>
              <FileText className={`${styles.inputIcon} ${styles.textareaIcon}`} size={16} />
              <textarea
                id="specialNotes"
                value={formData.specialNotes}
                onChange={(e) => onFieldChange('specialNotes', e.target.value)}
                placeholder={fieldPlaceholder('specialNotes', 'ecomOrders.specialNotesPlaceholder')}
                className={errors.specialNotes ? styles.error : ''}
                rows={2}
              />
            </div>
            {errors.specialNotes && <span className={styles.errorText}>{errors.specialNotes}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInformationSection;
